/**
 * Taking a row off Адреси за заключване, the two ways a row can be made of (#434).
 *
 * A booked row is orders, and orders are never deleted: Премахни от списъка stamps exactly the
 * row's bookings — in the period and before it — as no door worth locking, and nothing else
 * about them changes. It is undone from the notice (Върни), and a new booking typed the same
 * way brings the row back by itself, so the dialog only says how many; the page posts it once
 * the dialog has closed, and keeps the ids for Върни.
 *
 * An imported row is the old system's saved addresses, and Изтрий deletes them for good. Only
 * those nobody has used from here: one a car has been sent to is the firm's own record now, and
 * is listed but cannot be ticked — the server keeps it as well, and says why. There is no undo,
 * so the dialog names every address that goes and asks for Разбирам before the button wakes;
 * importing the same file again will not bring them back, because each leaves a tombstone.
 *
 * The members are read when the dialog opens, by the row's key, as Присъедини… reads them; a row
 * that has gone since is `report.row_gone`, and the page says so and reads the list again.
 *
 * The buttons are the dialog's footer, outside the part that scrolls, so a caller with many
 * addresses never pushes Изтрий out of sight.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@taxi/api-client';

import { customersApi, type ImportedAddressesRemoved } from '../orders/api';
import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import { Banner, Button, Dialog, EmptyState } from '../ui';
import { countOptions, dismissIds, removable, type DismissIds, type ImportedView, type UnplacedMember } from './addressesQuery';
import { reportsApi, type AddressToLock } from './api';
import type { SourceFilter } from './back';
import type { Period } from './period';

export function RemoveDialog({
  open,
  row,
  period,
  source,
  view,
  onClose,
  onDismiss,
  onRemoved,
  onGone,
}: {
  open: boolean;
  row: AddressToLock | null;
  /** The period the list was read over, for the members to be read the same way. */
  period: Period;
  source: SourceFilter;
  /** The view of Внесени the row was listed under. */
  view: ImportedView;
  onClose: () => void;
  /** A booked row: the bookings to stamp, for the page to post and to offer Върни over. */
  onDismiss: (ids: DismissIds, line: string) => void;
  /** An imported row: what the deletion did. */
  onRemoved: (result: ImportedAddressesRemoved, line: string) => void;
  /** The row has nothing in it any more — locked, or joined, since the list was read — with the words saying so. */
  onGone: (message: string) => void;
}) {
  // Mounted afresh each time it opens, dialog and all, so the members are the row's now,
  // nothing is ticked, and the form's state is at hand for the footer's buttons.
  return open && row !== null ? (
    <RemoveForm
      row={row}
      period={period}
      source={source}
      view={view}
      onClose={onClose}
      onDismiss={onDismiss}
      onRemoved={onRemoved}
      onGone={onGone}
    />
  ) : null;
}

function RemoveForm({
  row,
  period,
  source,
  view,
  onClose,
  onDismiss,
  onRemoved,
  onGone,
}: {
  row: AddressToLock;
  period: Period;
  source: SourceFilter;
  /** The view of Внесени the row was listed under. */
  view: ImportedView;
  onClose: () => void;
  onDismiss: (ids: DismissIds, line: string) => void;
  onRemoved: (result: ImportedAddressesRemoved, line: string) => void;
  onGone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [members, setMembers] = useState<UnplacedMember[] | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Read through a ref: the caller's callback is a fresh closure on every render, and the read
  // below must run once, not again on each.
  const gone = useRef(onGone);

  useEffect(() => {
    gone.current = onGone;
  });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const read = await reportsApi.unplacedMembers(period, row.key, source, view);

        if (cancelled) return;

        setMembers(read.members);
        setTicked(new Set(read.members.filter(removable).map((member) => member.id)));
      } catch (failure) {
        if (cancelled) return;

        const message = describe(failure, t, 'settings.couldNotLoad');

        // The page closes the dialog and says why there, where the words outlive it.
        if (failure instanceof ApiError && failure.problem.code === 'report.row_gone') gone.current(message);
        else setProblem(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [period, row.key, source, view, t]);

  function toggle(id: string) {
    setTicked((current) => {
      const next = new Set(current);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  }

  const title = t(row.imported ? 'reports.addresses.removeTitle' : 'reports.addresses.dismissTitle', { line: row.line });

  const shell = (body: ReactNode, confirm?: { label: string; disabled: boolean; onConfirm: () => void }) => (
    <Dialog
      open
      title={title}
      onClose={onClose}
      actions={
        confirm && (
          <>
            <Button variant="quiet" onClick={onClose}>
              {t('surcharges.cancelEdit')}
            </Button>
            <Button variant="danger" disabled={confirm.disabled} onClick={confirm.onConfirm}>
              {confirm.label}
            </Button>
          </>
        )
      }
    >
      {body}
    </Dialog>
  );

  if (members === null) {
    return shell(problem !== null ? <Banner tone="danger">{problem}</Banner> : <EmptyState>{t('app.loading')}</EmptyState>);
  }

  if (!row.imported) {
    const ids = dismissIds(members);
    const count = ids.pickups.length + ids.destinations.length;

    return shell(<p className="m-0 leading-[1.45]">{t('reports.addresses.dismissBody', countOptions(count, formats.count))}</p>, {
      label: t('reports.addresses.dismissConfirm'),
      disabled: count === 0,
      onConfirm: () => onDismiss(ids, row.line),
    });
  }

  const saved = members.filter((member) => member.kind === 'Saved');
  const chosen = saved.filter((member) => removable(member) && ticked.has(member.id));
  const callers = new Set(chosen.map((member) => member.customerId ?? member.id)).size;

  async function remove() {
    setBusy(true);
    setProblem(null);

    try {
      onRemoved(await customersApi.removeImportedAddresses(chosen.map((member) => member.id)), row.line);
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  return shell(
    <div className="flex flex-col gap-3">
      {problem !== null && <Banner tone="danger">{problem}</Banner>}

      {chosen.length === 0 && saved.every((member) => !removable(member)) ? (
        <p className="m-0 leading-[1.45]">{t('reports.addresses.removeNone')}</p>
      ) : (
        <p className="m-0 leading-[1.45]">
          {t('reports.addresses.removeBody', {
            addresses: t('counts.addresses', countOptions(chosen.length, formats.count)),
            callers: t('counts.callers', countOptions(callers, formats.count)),
          })}
        </p>
      )}

      <div className="flex flex-col gap-1">
        {saved.map((member) => {
          const allowed = removable(member);

          return (
            <label key={member.id} className="flex items-start gap-2 text-body">
              <input
                type="checkbox"
                className="mt-1 accent-brand"
                checked={allowed && ticked.has(member.id)}
                disabled={!allowed || busy}
                onChange={() => toggle(member.id)}
              />
              <span className="flex flex-col min-w-0">
                <span>
                  {member.typed}
                  {member.notes != null && member.notes !== '' && <span className="text-ink-3"> · {member.notes}</span>}
                </span>
                <span className="text-secondary text-ink-3">
                  {[member.caller ?? member.phone, formats.moment(member.at), allowed ? null : t('reports.addresses.usedHere')]
                    .filter((part) => part != null && part !== '')
                    .join(' · ')}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      <label className="flex items-center gap-2 text-body font-semibold">
        <input
          type="checkbox"
          className="accent-brand"
          checked={understood}
          disabled={busy || chosen.length === 0}
          onChange={(event) => setUnderstood(event.target.checked)}
        />
        {t('reports.addresses.removeUnderstand')}
      </label>
    </div>,
    {
      label: t('reports.addresses.removeConfirm'),
      disabled: busy || !understood || chosen.length === 0,
      onConfirm: () => void remove(),
    },
  );
}
